/**
 * Settings — the scoring configuration. ADR-0009.
 *
 * ADR-0009 calls these business thresholds rather than constants, chosen
 * before anyone had seen real data and expected to be recalibrated against a
 * design partner's actual portfolio. The test it sets is concrete: does the
 * top of the ranked list match what an experienced delivery lead would have
 * escalated by hand? If not, retune the floor and the multiplier *before*
 * touching the formula's shape — changing that shape supersedes the ADR
 * rather than tuning it, which is why this screen exposes the knobs and not
 * the equation.
 *
 * Account details are deliberately absent: nothing here is a user-level
 * setting, and a read-only copy of the address already in the navbar was a
 * page pretending to offer something it did not.
 */
import { useEffect, useState } from 'react';
import { formatCents, parseMoneyToCents } from '@picklii/recon';
import { DEFAULT_STORED_SEVERITY_CONFIG, type StoredSeverityConfig } from '@picklii/shared';
import { AppShell } from '../components/AppShell';
import { ApiError, api } from '../lib/api';

/** Thresholds are cents on the wire and dollars in the field. */
interface Draft {
	burnFloor: string;
	directionMultiplier: string;
	criticalCents: string;
	highCents: string;
	mediumCents: string;
}

function toDraft(config: StoredSeverityConfig): Draft {
	const dollars = (c: number) => (c / 100).toFixed(2);
	return {
		burnFloor: String(config.burnFloor),
		directionMultiplier: String(config.directionMultiplier),
		criticalCents: dollars(config.criticalCents),
		highCents: dollars(config.highCents),
		mediumCents: dollars(config.mediumCents),
	};
}

const BANDS: ReadonlyArray<{
	key: 'criticalCents' | 'highCents' | 'mediumCents';
	label: string;
	hint: string;
}> = [
	{ key: 'criticalCents', label: 'Critical at or above', hint: 'The five-alarm fires.' },
	{ key: 'highCents', label: 'High at or above', hint: 'Must be below Critical.' },
	{
		key: 'mediumCents',
		label: 'Medium at or above',
		hint: 'Below this is Low. Must be above zero.',
	},
];

export function Settings() {
	const [draft, setDraft] = useState<Draft | null>(null);
	const [saved, setSaved] = useState<StoredSeverityConfig | null>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [issues, setIssues] = useState<string[]>([]);
	const [justSaved, setJustSaved] = useState(false);

	useEffect(() => {
		api.getSeverityConfig().then(
			(config) => {
				setSaved(config);
				setDraft(toDraft(config));
			},
			(cause: Error) => setError(cause.message),
		);
	}, []);

	function set(key: keyof Draft, value: string) {
		setDraft((current) => (current === null ? current : { ...current, [key]: value }));
		setJustSaved(false);
		setIssues([]);
	}

	async function save() {
		if (draft === null) return;
		// Money through parseMoneyToCents, never parseFloat * 100 — the whole
		// product rests on its dollar figures being exact. ADR-0002.
		const next = {
			burnFloor: Number(draft.burnFloor),
			directionMultiplier: Number(draft.directionMultiplier),
			criticalCents: parseMoneyToCents(draft.criticalCents) ?? -1,
			highCents: parseMoneyToCents(draft.highCents) ?? -1,
			mediumCents: parseMoneyToCents(draft.mediumCents) ?? -1,
		};

		setBusy(true);
		setError(null);
		setIssues([]);
		try {
			const result = await api.saveSeverityConfig(next);
			setSaved(result);
			setDraft(toDraft(result));
			setJustSaved(true);
		} catch (cause) {
			if (cause instanceof ApiError && Array.isArray(cause.details)) {
				setIssues(
					(cause.details as Array<{ message?: string }>).map((d) => d.message ?? 'Invalid value'),
				);
			}
			setError(cause instanceof Error ? cause.message : 'Could not save.');
		} finally {
			setBusy(false);
		}
	}

	const dirty =
		draft !== null && saved !== null && JSON.stringify(draft) !== JSON.stringify(toDraft(saved));

	return (
		<AppShell title="Settings">
			{error !== null && (
				<div className="alert alert-error alert-soft col-span-12" role="alert">
					<div>
						<span>{error}</span>
						{issues.length > 0 && (
							<ul className="mt-1 list-inside list-disc text-sm">
								{issues.map((m, i) => (
									<li key={i}>{m}</li>
								))}
							</ul>
						)}
					</div>
				</div>
			)}

			{draft === null && error === null && (
				<div className="col-span-12 flex justify-center py-16">
					<span className="loading loading-spinner" />
				</div>
			)}

			{draft !== null && (
				<>
					<section className="card bg-base-200 border-base-300 col-span-12 border shadow-xs">
						<div className="card-body gap-4">
							<div>
								<h2 className="card-title text-base">Severity bands</h2>
								<p className="text-base-content/70 text-sm">
									Where a finding lands once it is scored. Severity is exposure weighted by
									how much of the budget is already spent and by direction — it carries
									dollar magnitude but is not a dollar amount.
								</p>
							</div>

							<div className="grid gap-4 md:grid-cols-3">
								{BANDS.map((band) => (
									<label key={band.key} className="flex flex-col gap-1.5">
										<span className="text-sm font-medium">{band.label}</span>
										<label className="input input-bordered input-sm flex items-center gap-2">
											<span className="text-base-content/70">$</span>
											<input
												type="text"
												inputMode="decimal"
												className="grow"
												value={draft[band.key]}
												onChange={(e) => set(band.key, e.target.value)}
												aria-label={band.label}
											/>
										</label>
										<span className="text-base-content/70 text-xs">{band.hint}</span>
									</label>
								))}
							</div>
						</div>
					</section>

					<section className="card bg-base-200 border-base-300 col-span-12 border shadow-xs">
						<div className="card-body gap-4">
							<div>
								<h2 className="card-title text-base">Scoring weights</h2>
								<p className="text-base-content/70 text-sm">
									severity = exposure × (floor + (1 − floor) × burn) × direction
								</p>
							</div>

							<div className="grid gap-4 md:grid-cols-2">
								<label className="flex flex-col gap-1.5">
									<span className="text-sm font-medium">Exposure floor</span>
									<input
										type="text"
										inputMode="decimal"
										className="input input-bordered input-sm"
										value={draft.burnFloor}
										onChange={(e) => set('burnFloor', e.target.value)}
										aria-label="Exposure floor"
									/>
									<span className="text-base-content/70 text-xs">
										Between 0 and 1, and never 0. It is why a large error on a project that
										has not started still outranks a small one nearly finished — at zero, a
										brand-new project scores nothing however wrong its budget is.
									</span>
								</label>

								<label className="flex flex-col gap-1.5">
									<span className="text-sm font-medium">Margin-leakage multiplier</span>
									<input
										type="text"
										inputMode="decimal"
										className="input input-bordered input-sm"
										value={draft.directionMultiplier}
										onChange={(e) => set('directionMultiplier', e.target.value)}
										aria-label="Margin-leakage multiplier"
									/>
									<span className="text-base-content/70 text-xs">
										Applied when the PSA budget is too high — the team burning hours nobody
										bought. 1 treats both directions alike.
									</span>
								</label>
							</div>
						</div>
					</section>

					<section className="col-span-12 flex flex-wrap items-center justify-end gap-3">
						<span className="text-base-content/70 me-auto text-sm">
							{/* Stated plainly, because it is the surprising part. */}
							Applies to future reconciliations. Past runs keep the bands they were given —
							a finished reconciliation is a record of what was found at the time.
						</span>
						{justSaved && !dirty && <span className="badge badge-success badge-sm">Saved</span>}
						<button
							className="btn btn-sm btn-outline"
							disabled={busy}
							onClick={() => {
								setDraft(toDraft(DEFAULT_STORED_SEVERITY_CONFIG));
								setJustSaved(false);
							}}>
							Reset to defaults
						</button>
						<button
							className="btn btn-primary btn-sm"
							disabled={busy || !dirty}
							onClick={() => void save()}>
							{busy ? <span className="loading loading-spinner loading-xs" /> : 'Save thresholds'}
						</button>
					</section>

					{saved !== null && (
						<p className="text-base-content/70 col-span-12 text-xs">
							In effect now: Critical ≥ {formatCents(saved.criticalCents)} · High ≥{' '}
							{formatCents(saved.highCents)} · Medium ≥ {formatCents(saved.mediumCents)} · floor{' '}
							{saved.burnFloor} · multiplier {saved.directionMultiplier}×
						</p>
					)}
				</>
			)}
		</AppShell>
	);
}
